// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CommandPalette,
  useCommandPaletteShortcut,
  useToast,
  type ToastVariant,
} from '@intakeform/ui';
import { ClipboardList, FileText, LayoutDashboard, LogOut, Plus, SunMoon } from 'lucide-react';
import { useQuestionnaires } from '../api/questionnaires.js';
import { useResponses } from '../api/responses.js';
import { useLogout } from '../api/auth.js';
import { useTheme } from '../theme/index.js';

/**
 * Cmd+K palette. Mounted once inside the clinician shell so the binding
 * is scoped to authenticated routes (the patient `/intake/:token` flow
 * intentionally does not get it — it would clutter the focus and the
 * palette's "Sign out" / "Open form" actions are clinician-only).
 *
 * The 6 quick actions enumerated in in-zo2: dashboard, new form, open
 * form by title, open response by id, toggle theme, sign out. The form
 * + response lookups are populated from the already-cached react-query
 * lists so the palette doesn't fire a fresh fetch on open.
 */
export function ClinicianCommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const forms = useQuestionnaires();
  const responses = useResponses();
  const { mode, toggle: toggleTheme } = useTheme();
  const logout = useLogout();
  const { toast } = useToast();

  useCommandPaletteShortcut(setOpen);

  const close = (): void => setOpen(false);

  const notify = (title: string, variant: ToastVariant = 'default'): void => {
    toast({ title, variant });
  };

  const formItems = useMemo(() => forms.data ?? [], [forms.data]);
  const responseItems = useMemo(() => responses.data ?? [], [responses.data]);

  return (
    <CommandPalette open={open} onOpenChange={setOpen}>
      <CommandPalette.Group heading="Navigate">
        <CommandPalette.Action
          label="Go to dashboard"
          keywords={['home', 'index']}
          icon={<LayoutDashboard size={16} />}
          shortcut="G D"
          onSelect={() => {
            navigate('/dashboard');
            close();
          }}
        />
        <CommandPalette.Action
          label="Create new form"
          keywords={['questionnaire', 'builder', 'add']}
          icon={<Plus size={16} />}
          shortcut="N F"
          onSelect={() => {
            navigate('/builder');
            close();
          }}
        />
      </CommandPalette.Group>

      <CommandPalette.Group heading="Open">
        {formItems.length === 0 ? (
          <CommandPalette.Action
            label="Open form by title — no forms yet"
            keywords={['form']}
            icon={<FileText size={16} />}
            onSelect={() => {
              notify('Create a form first', 'info');
              close();
            }}
          />
        ) : (
          formItems.map((f) => (
            <CommandPalette.Action
              key={f.id}
              label={`Open form: ${f.title}`}
              keywords={[f.id, 'form', String(f.version), f.status]}
              icon={<FileText size={16} />}
              onSelect={() => {
                navigate(`/builder/${f.id}`);
                close();
              }}
            />
          ))
        )}

        {responseItems.length === 0 ? (
          <CommandPalette.Action
            label="Open response — no responses yet"
            keywords={['response']}
            icon={<ClipboardList size={16} />}
            onSelect={() => {
              notify('Issue a magic link to collect a response', 'info');
              close();
            }}
          />
        ) : (
          responseItems.slice(0, 20).map((r) => (
            <CommandPalette.Action
              key={r.id}
              label={`Open response: ${r.id.slice(0, 8)}`}
              keywords={[r.id, r.status, r.flagged ? 'flagged' : '']}
              icon={<ClipboardList size={16} />}
              onSelect={() => {
                navigate(`/responses/${r.id}`);
                close();
              }}
            />
          ))
        )}
      </CommandPalette.Group>

      <CommandPalette.Group heading="Preferences">
        <CommandPalette.Action
          label="Toggle theme"
          keywords={['dark', 'light', 'mode', 'appearance']}
          icon={<SunMoon size={16} />}
          shortcut="T"
          onSelect={() => {
            toggleTheme();
            notify(mode === 'dark' ? 'Switched to light theme' : 'Switched to dark theme');
            close();
          }}
        />
      </CommandPalette.Group>

      <CommandPalette.Group heading="Session">
        <CommandPalette.Action
          label="Sign out"
          keywords={['logout', 'exit']}
          icon={<LogOut size={16} />}
          onSelect={() => {
            logout.mutate(undefined);
            close();
          }}
        />
      </CommandPalette.Group>
    </CommandPalette>
  );
}
